import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Eye,
  FileText,
  Globe,
  Loader2,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Settings2,
  Trash2,
  X,
} from 'lucide-react';

import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import { resolveSeo } from '../../../Shared/Seo/seoHead';
import { resetSeoConfigCache } from '../../../Shared/Seo/useSeoConfig';
import {
  getSeoPageConfigs,
  getSeoPublicConfig,
  resetSeoPageConfig,
  saveSeoPageConfig,
  updateSeoSiteSettings,
} from '../../Services/SeoService';
import {
  CHANGE_FREQUENCIES,
  OG_TYPES,
  SeoPageConfig,
  SeoPageConfigRequest,
  SeoSiteSettings,
  STRUCTURED_DATA_TYPES,
} from '../../Types/SeoType';

const labelClass = 'mb-1.5 block text-[10px] font-black uppercase tracking-widest text-slate-400';
const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';

const defaultSite: SeoSiteSettings = {
  baseUrl: 'https://nexgent.tech',
  siteName: 'NexGent',
  defaultTitle: 'NexGent | Private Leadership Hiring Platform',
  defaultDescription:
    'NexGent is an invitation-only hiring platform where senior leaders set their terms and let top teams apply to hire them.',
  ogImage: '',
  twitterHandle: '',
  organizationLegalName: 'NexGent',
  organizationLogo: '',
  organizationDescription:
    'NexGent is an invitation-only hiring platform where senior leaders set their terms and let top teams apply to hire them.',
  locale: 'en_US',
  googleSiteVerification: '',
  indexingEnabled: true,
  sitemapEnabled: true,
  extraRobotsRules: '',
};

const emptyPage: SeoPageConfig = {
  key: '',
  path: '',
  label: '',
  title: '',
  description: '',
  canonicalUrl: '',
  ogImage: '',
  ogType: 'website',
  indexable: true,
  sitemapInclude: true,
  priority: 0.5,
  changeFreq: 'monthly',
  structuredDataType: 'NONE',
  structuredDataJson: '',
  lastModified: '',
  customized: false,
};

type ToggleProps = {
  label: string;
  help: string;
  value: boolean;
  onChange: (value: boolean) => void;
};

const Toggle = ({ label, help, value, onChange }: ToggleProps) => (
  <label
    className={`flex items-start gap-3 rounded-2xl border border-slate-800 bg-slate-950/50 px-4 py-3 transition ${
      value ? 'hover:border-cyan-500/30' : 'opacity-60'
    }`}
  >
    <input
      type="checkbox"
      checked={value}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 h-4 w-4 shrink-0 accent-cyan-500"
    />
    <span className="min-w-0">
      <span className="block text-sm font-bold text-slate-100">{label}</span>
      <span className="block text-[11px] font-semibold text-slate-500">{help}</span>
    </span>
  </label>
);

function SeoSetupComponent() {
  const [tab, setTab] = useState<'site' | 'pages' | 'preview'>('site');
  const [site, setSite] = useState<SeoSiteSettings>(defaultSite);
  const [originalSite, setOriginalSite] = useState<SeoSiteSettings>(defaultSite);
  const [pages, setPages] = useState<SeoPageConfig[]>([]);
  const [originalPages, setOriginalPages] = useState<SeoPageConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [editing, setEditing] = useState<SeoPageConfig | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [previewPath, setPreviewPath] = useState('/');

  const showAlert = (variant: AlertProps['variant'], title: string, message: string) =>
    setAlert({ variant, title, message, autoCloseMs: 5000, onClose: () => setAlert(null) });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [siteResponse, pagesResponse] = await Promise.all([
        getSeoPublicConfig(),
        getSeoPageConfigs(),
      ]);

      if (siteResponse.success && siteResponse.data?.site) {
        setSite({ ...defaultSite, ...siteResponse.data.site });
        setOriginalSite({ ...defaultSite, ...siteResponse.data.site });
      }
      if (pagesResponse.success && pagesResponse.data) {
        setPages(pagesResponse.data);
        setOriginalPages(pagesResponse.data);
      } else {
        showAlert('error', 'Load Error', pagesResponse.message || 'Failed to load the page configurations.');
      }
    } catch {
      showAlert('error', 'Load Error', 'Failed to load the SEO configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const siteDirty = JSON.stringify(site) !== JSON.stringify(originalSite);

  const handleSaveSite = async () => {
    if (site.baseUrl && !/^https?:\/\//i.test(site.baseUrl.trim())) {
      showAlert('error', 'Invalid Base URL', 'The base URL has to start with http:// or https://.');
      return;
    }
    setSaving(true);
    try {
      const response = await updateSeoSiteSettings(site);
      if (response.success && response.data) {
        setSite({ ...defaultSite, ...response.data });
        setOriginalSite({ ...defaultSite, ...response.data });
        resetSeoConfigCache();
        showAlert('success', 'Saved', 'Site settings updated. The public pages pick them up on the next load.');
      } else {
        showAlert('error', 'Save Error', response.message || 'Failed to save the site settings.');
      }
    } catch {
      showAlert('error', 'Save Error', 'Failed to save the site settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePage = async () => {
    if (!editing) return;

    if (!editing.path.trim().startsWith('/')) {
      showAlert('error', 'Invalid Path', 'A page path has to start with a slash, for example /about.');
      return;
    }
    if (editing.structuredDataType !== 'NONE' && editing.structuredDataJson.trim()) {
      try {
        JSON.parse(editing.structuredDataJson);
      } catch {
        showAlert('error', 'Invalid JSON', 'The structured data is not valid JSON, so it was not saved.');
        return;
      }
    }

    const payload: SeoPageConfigRequest = {
      pageKey: editing.key || undefined,
      path: editing.path.trim(),
      label: editing.label,
      title: editing.title,
      description: editing.description,
      canonicalUrl: editing.canonicalUrl,
      ogImage: editing.ogImage,
      ogType: editing.ogType,
      indexable: editing.indexable,
      sitemapInclude: editing.sitemapInclude,
      priority: editing.priority,
      changeFreq: editing.changeFreq,
      structuredDataType: editing.structuredDataType,
      structuredDataJson: editing.structuredDataJson,
      lastModified: editing.lastModified,
    };

    setSaving(true);
    try {
      const response = await saveSeoPageConfig(payload);
      if (response.success) {
        setEditing(null);
        resetSeoConfigCache();
        await load();
        showAlert('success', 'Saved', 'The page configuration was updated.');
      } else {
        showAlert('error', 'Save Error', response.message || 'Failed to save the page configuration.');
      }
    } catch {
      showAlert('error', 'Save Error', 'Failed to save the page configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPage = async (page: SeoPageConfig) => {
    setSaving(true);
    try {
      const response = await resetSeoPageConfig(page.key);
      if (response.success) {
        resetSeoConfigCache();
        await load();
        showAlert('success', 'Reset', `${page.label} is back on the default configuration.`);
      } else {
        showAlert('error', 'Reset Error', response.message || 'Failed to reset the page configuration.');
      }
    } catch {
      showAlert('error', 'Reset Error', 'Failed to reset the page configuration.');
    } finally {
      setSaving(false);
    }
  };

  const openNewPage = () => {
    setEditing({ ...emptyPage });
    setIsNew(true);
  };

  const openPage = (page: SeoPageConfig) => {
    setEditing({ ...page });
    setIsNew(false);
  };

  const preview = useMemo(() => {
    const resolved = resolveSeo({ site, pages }, previewPath);
    return resolved;
  }, [site, pages, previewPath]);

  const tabs = [
    { key: 'site' as const, label: 'Site Settings', icon: Settings2 },
    { key: 'pages' as const, label: 'Page Configs', icon: FileText },
    { key: 'preview' as const, label: 'Preview', icon: Eye },
  ];

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Search size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">SEO Setup</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Titles, descriptions, canonical URLs, Open Graph, sitemap and robots
            </p>
          </div>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <button
            onClick={load}
            title="Refresh"
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          {tab === 'site' && (
            <button
              onClick={handleSaveSite}
              disabled={saving || loading || !siteDirty}
              className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save Changes
            </button>
          )}
        </div>
      </div>

      {alert && (
        <div className="mb-4">
          <Alert {...alert} onClose={() => setAlert(null)} />
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-2">
        {tabs.map((item) => {
          const Icon = item.icon;
          const active = tab === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-xs font-black uppercase tracking-widest transition ${
                active
                  ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-300'
                  : 'border-slate-800 bg-slate-900/60 text-slate-400 hover:border-slate-700 hover:text-slate-200'
              }`}
            >
              <Icon size={14} />
              {item.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
        </div>
      ) : (
        <>
          {/* ================= SITE SETTINGS ================= */}
          {tab === 'site' && (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
                <header className="mb-4 flex items-center gap-2">
                  <Globe size={16} className="text-cyan-400" />
                  <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">Identity</h2>
                </header>

                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>Base URL</label>
                    <input
                      value={site.baseUrl}
                      onChange={(e) => setSite({ ...site, baseUrl: e.target.value })}
                      placeholder="https://nexgent.tech"
                      className={fieldClass}
                    />
                    <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                      Every canonical URL, Open Graph URL and the sitemap entry are built from this.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelClass}>Site Name</label>
                      <input
                        value={site.siteName}
                        onChange={(e) => setSite({ ...site, siteName: e.target.value })}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Locale</label>
                      <input
                        value={site.locale}
                        onChange={(e) => setSite({ ...site, locale: e.target.value })}
                        placeholder="en_US"
                        className={fieldClass}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Default Title</label>
                    <input
                      value={site.defaultTitle}
                      onChange={(e) => setSite({ ...site, defaultTitle: e.target.value })}
                      className={fieldClass}
                    />
                    <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                      Used when a page has no title of its own.
                    </p>
                  </div>

                  <div>
                    <label className={labelClass}>Default Meta Description</label>
                    <textarea
                      value={site.defaultDescription}
                      onChange={(e) => setSite({ ...site, defaultDescription: e.target.value })}
                      rows={3}
                      className={fieldClass}
                    />
                    <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                      {site.defaultDescription.length} characters. Around 150 reads best in a search result.
                    </p>
                  </div>

                  <div>
                    <label className={labelClass}>Open Graph Image URL</label>
                    <input
                      value={site.ogImage}
                      onChange={(e) => setSite({ ...site, ogImage: e.target.value })}
                      placeholder="https://nexgent.tech/og-cover.png"
                      className={fieldClass}
                    />
                    <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                      Falls back to this when a page has no image of its own. Use a 1200x630 image.
                    </p>
                  </div>

                  <div>
                    <label className={labelClass}>Twitter Handle</label>
                    <input
                      value={site.twitterHandle}
                      onChange={(e) => setSite({ ...site, twitterHandle: e.target.value })}
                      placeholder="@nexgent"
                      className={fieldClass}
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
                <header className="mb-4 flex items-center gap-2">
                  <Search size={16} className="text-cyan-400" />
                  <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">Crawling</h2>
                </header>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-3">
                    <Toggle
                      label="Allow Indexing"
                      help="Master switch. Off means every page is served as noindex, whatever the page says."
                      value={site.indexingEnabled}
                      onChange={(value) => setSite({ ...site, indexingEnabled: value })}
                    />
                    <Toggle
                      label="Publish The Sitemap"
                      help="Adds the sitemap to the sitemap list and publishes /sitemap.xml."
                      value={site.sitemapEnabled}
                      onChange={(value) => setSite({ ...site, sitemapEnabled: value })}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Extra Robots Rules</label>
                    <input
                      value={site.extraRobotsRules}
                      onChange={(e) => setSite({ ...site, extraRobotsRules: e.target.value })}
                      placeholder="index, follow"
                      className={fieldClass}
                    />
                    <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                      Appended to the robots directive of every indexable page.
                    </p>
                  </div>

                  <div>
                    <label className={labelClass}>Google Site Verification</label>
                    <input
                      value={site.googleSiteVerification}
                      onChange={(e) => setSite({ ...site, googleSiteVerification: e.target.value })}
                      placeholder="paste the code from Search Console"
                      className={fieldClass}
                    />
                    <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                      Published as the google-site-verification meta tag.
                    </p>
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
                <header className="mb-4 flex items-center gap-2">
                  <Globe size={16} className="text-cyan-400" />
                  <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">
                    Organization Schema
                  </h2>
                </header>

                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>Legal Name</label>
                    <input
                      value={site.organizationLegalName}
                      onChange={(e) => setSite({ ...site, organizationLegalName: e.target.value })}
                      className={fieldClass}
                    />
                    <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                      Only publish a legal name that is registered for the business.
                    </p>
                  </div>

                  <div>
                    <label className={labelClass}>Logo URL</label>
                    <input
                      value={site.organizationLogo}
                      onChange={(e) => setSite({ ...site, organizationLogo: e.target.value })}
                      placeholder="https://nexgent.tech/logo512.png"
                      className={fieldClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Organization Description</label>
                    <textarea
                      value={site.organizationDescription}
                      onChange={(e) => setSite({ ...site, organizationDescription: e.target.value })}
                      rows={3}
                      className={fieldClass}
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
                <header className="mb-4 flex items-center gap-2">
                  <FileText size={16} className="text-cyan-400" />
                  <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">
                    Crawler Files
                  </h2>
                </header>

                <div className="space-y-3 text-[11px] font-semibold text-slate-400">
                  <p>
                    The sitemap and robots file are generated from this configuration, so a page that is
                    switched off disappears from the sitemap on the next request - no deployment needed.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href="/sitemap.xml"
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-1.5 font-mono text-[10px] text-cyan-300 transition hover:border-cyan-500/40"
                    >
                      /sitemap.xml
                    </a>
                    <a
                      href="/robots.txt"
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-1.5 font-mono text-[10px] text-cyan-300 transition hover:border-cyan-500/40"
                    >
                      /robots.txt
                    </a>
                  </div>
                  <p className="text-slate-600">
                    Those paths are served by nginx from the backend once the proxy rule is in place. Until
                    then the static copies that ship with the build are used.
                  </p>
                </div>
              </section>
            </div>
          )}

          {/* ================= PAGE CONFIGS ================= */}
          {tab === 'pages' && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50">
              <header className="flex flex-col gap-3 border-b border-slate-800 p-5 sm:flex-row sm:items-center">
                <div>
                  <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">
                    Public Pages
                  </h2>
                  <p className="text-[11px] font-semibold text-slate-500">
                    {pages.length} pages. A page that is not listed here is served as noindex.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openNewPage}
                  className="inline-flex items-center gap-2 rounded-xl border border-cyan-500/40 bg-cyan-500/10 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-300 transition hover:bg-cyan-500/20 sm:ml-auto"
                >
                  <Plus size={14} />
                  Add Page
                </button>
              </header>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-left text-xs">
                  <thead className="bg-slate-950/60 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Page</th>
                      <th className="px-4 py-3">Title</th>
                      <th className="px-4 py-3">Index</th>
                      <th className="px-4 py-3">Sitemap</th>
                      <th className="px-4 py-3">Priority</th>
                      <th className="px-4 py-3">Source</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {pages.map((page) => (
                      <tr key={page.key} className="align-top transition hover:bg-slate-900/60">
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openPage(page)}
                            className="text-left"
                          >
                            <span className="block font-bold text-slate-100">{page.label || page.path}</span>
                            <span className="block font-mono text-[10px] text-cyan-400">{page.path}</span>
                          </button>
                        </td>
                        <td className="max-w-[280px] px-4 py-3 font-semibold text-slate-400">
                          <span className="line-clamp-2">{page.title || '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`rounded-md border px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${
                              page.indexable
                                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                                : 'border-rose-500/30 bg-rose-500/10 text-rose-300'
                            }`}
                          >
                            {page.indexable ? 'Index' : 'Noindex'}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-500">
                          {page.sitemapInclude ? 'Included' : 'Excluded'}
                        </td>
                        <td className="px-4 py-3 font-mono text-slate-400">{page.priority.toFixed(1)}</td>
                        <td className="px-4 py-3 font-semibold text-slate-500">
                          {page.customized ? 'Custom' : 'Default'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => openPage(page)}
                              title="Edit"
                              className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-300"
                            >
                              <Settings2 size={14} />
                            </button>
                            {page.customized && (
                              <button
                                type="button"
                                onClick={() => handleResetPage(page)}
                                title="Reset to default"
                                disabled={saving}
                                className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-amber-500/40 hover:text-amber-300 disabled:opacity-50"
                              >
                                <RotateCcw size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pages.length === 0 && (
                <p className="p-8 text-center text-xs font-semibold text-slate-500">
                  No pages are configured yet.
                </p>
              )}
            </div>
          )}

          {/* ================= PREVIEW ================= */}
          {tab === 'preview' && (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
                <header className="mb-4 flex items-center gap-2">
                  <Eye size={16} className="text-cyan-400" />
                  <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">Head Preview</h2>
                </header>

                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>Page</label>
                    <select
                      value={previewPath}
                      onChange={(e) => setPreviewPath(e.target.value)}
                      className={fieldClass}
                    >
                      {pages.map((page) => (
                        <option key={page.key} value={page.path}>
                          {page.label || page.path} — {page.path}
                        </option>
                      ))}
                      <option value="/admin/dashboard">/admin/dashboard (not registered)</option>
                      <option value="/candidate/dashboard">/candidate/dashboard (not registered)</option>
                    </select>
                  </div>

                  <div className="space-y-2 rounded-2xl border border-slate-800 bg-slate-950/60 p-4 font-mono text-[11px] leading-relaxed text-slate-400">
                    <p className="text-slate-500">&lt;title&gt;{preview.title}&lt;/title&gt;</p>
                    <p className="text-slate-500">
                      &lt;meta name="description" content="{preview.description}" /&gt;
                    </p>
                    <p className={preview.indexable ? 'text-emerald-400' : 'text-rose-400'}>
                      &lt;meta name="robots" content="{preview.robots}" /&gt;
                    </p>
                    <p className="text-slate-500">
                      &lt;link rel="canonical" href={preview.canonical || '(none)'} /&gt;
                    </p>
                    <p className="text-slate-500">
                      &lt;meta property="og:title" content="{preview.ogTitle}" /&gt;
                    </p>
                    <p className="text-slate-500">
                      &lt;meta property="og:description" content="{preview.ogDescription}" /&gt;
                    </p>
                    <p className="text-slate-500">
                      &lt;meta property="og:url" content={preview.ogUrl || '(none)'} /&gt;
                    </p>
                    <p className="text-slate-500">
                      &lt;meta property="og:type" content="{preview.ogType}" /&gt;
                    </p>
                    <p className="text-slate-500">
                      &lt;meta property="og:image" content={preview.ogImage || '(none)'} /&gt;
                    </p>
                    <p className="text-slate-500">
                      &lt;meta name="twitter:card" content="{preview.twitterCard}" /&gt;
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      Search result
                    </p>
                    <p className="mt-2 truncate text-lg text-blue-300">{preview.title}</p>
                    <p className="mt-0.5 truncate text-xs text-emerald-400">
                      {preview.canonical || 'no canonical url'}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">{preview.description}</p>
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
                <header className="mb-4 flex items-center gap-2">
                  <FileText size={16} className="text-cyan-400" />
                  <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">
                    Structured Data
                  </h2>
                </header>

                <p className="mb-4 text-[11px] font-semibold text-slate-500">
                  The Organization and WebSite schemas are published on every indexable page. A page can
                  add its own schema on the Page Configs tab.
                </p>

                <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-2xl border border-slate-800 bg-slate-950/60 p-4 font-mono text-[10px] leading-relaxed text-slate-400">
                  {preview.indexable
                    ? JSON.stringify(
                        {
                          '@context': 'https://schema.org',
                          '@type': 'Organization',
                          name: site.siteName,
                          url: `${site.baseUrl.replace(/\/+$/, '')}/`,
                          legalName: site.organizationLegalName,
                          description: site.organizationDescription,
                          logo: site.organizationLogo || undefined,
                        },
                        null,
                        2,
                      )
                    : 'No structured data is published on a page that is not indexable.'}
                </pre>
              </section>
            </div>
          )}
        </>
      )}

      {/* ================= PAGE EDITOR ================= */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/80 p-4 backdrop-blur-sm"
            onClick={() => setEditing(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
              className="my-8 w-full max-w-3xl rounded-2xl border border-slate-800 bg-slate-900 p-5 shadow-2xl"
            >
              <header className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-black uppercase tracking-widest text-slate-200">
                  {isNew ? 'Add Page' : `Edit ${editing.label || editing.path}`}
                </h3>
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="rounded-lg border border-slate-800 p-2 text-slate-400 transition hover:border-rose-500/40 hover:text-rose-300"
                >
                  <X size={14} />
                </button>
              </header>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Path</label>
                  <input
                    value={editing.path}
                    onChange={(e) => setEditing({ ...editing, path: e.target.value })}
                    placeholder="/about"
                    className={fieldClass}
                  />
                  <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                    The route this configuration belongs to.
                  </p>
                </div>

                <div>
                  <label className={labelClass}>Label</label>
                  <input
                    value={editing.label}
                    onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                    placeholder="About NexGent"
                    className={fieldClass}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className={labelClass}>Title</label>
                  <input
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    className={fieldClass}
                  />
                  <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                    {editing.title.length} characters. Around 60 reads best in a search result.
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <label className={labelClass}>Meta Description</label>
                  <textarea
                    value={editing.description}
                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    rows={3}
                    className={fieldClass}
                  />
                  <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                    {editing.description.length} characters. Around 150 reads best in a search result.
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <label className={labelClass}>Canonical URL</label>
                  <input
                    value={editing.canonicalUrl}
                    onChange={(e) => setEditing({ ...editing, canonicalUrl: e.target.value })}
                    placeholder="Leave blank to use the base URL and the path"
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Open Graph Image</label>
                  <input
                    value={editing.ogImage}
                    onChange={(e) => setEditing({ ...editing, ogImage: e.target.value })}
                    placeholder="Leave blank to use the site image"
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Open Graph Type</label>
                  <select
                    value={editing.ogType || 'website'}
                    onChange={(e) => setEditing({ ...editing, ogType: e.target.value })}
                    className={fieldClass}
                  >
                    {OG_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:col-span-2">
                  <Toggle
                    label="Indexable"
                    help="Off serves the page as noindex, nofollow."
                    value={editing.indexable}
                    onChange={(value) => setEditing({ ...editing, indexable: value })}
                  />
                  <Toggle
                    label="In Sitemap"
                    help="Off keeps the page out of sitemap.xml."
                    value={editing.sitemapInclude}
                    onChange={(value) => setEditing({ ...editing, sitemapInclude: value })}
                  />
                </div>

                <div>
                  <label className={labelClass}>Priority</label>
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.1}
                    value={editing.priority}
                    onChange={(e) =>
                      setEditing({ ...editing, priority: Math.min(1, Math.max(0, Number(e.target.value) || 0)) })
                    }
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Change Frequency</label>
                  <select
                    value={editing.changeFreq}
                    onChange={(e) => setEditing({ ...editing, changeFreq: e.target.value })}
                    className={fieldClass}
                  >
                    {CHANGE_FREQUENCIES.map((freq) => (
                      <option key={freq} value={freq}>
                        {freq}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Structured Data Type</label>
                  <select
                    value={editing.structuredDataType || 'NONE'}
                    onChange={(e) => setEditing({ ...editing, structuredDataType: e.target.value })}
                    className={fieldClass}
                  >
                    {STRUCTURED_DATA_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Last Modified</label>
                  <input
                    type="date"
                    value={editing.lastModified}
                    onChange={(e) => setEditing({ ...editing, lastModified: e.target.value })}
                    className={fieldClass}
                  />
                </div>

                {editing.structuredDataType !== 'NONE' && (
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Structured Data JSON</label>
                    <textarea
                      value={editing.structuredDataJson}
                      onChange={(e) => setEditing({ ...editing, structuredDataJson: e.target.value })}
                      rows={6}
                      placeholder='{"@type":"FAQPage","mainEntity":[...]}'
                      className={`${fieldClass} font-mono text-[11px]`}
                    />
                    <p className="mt-1.5 text-[11px] font-semibold text-slate-500">
                      A raw JSON-LD object published on this page. Leave blank to use the built in WebPage
                      schema.
                    </p>
                  </div>
                )}
              </div>

              <footer className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-300 transition hover:border-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePage}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Save Page
                </button>
              </footer>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {siteDirty && tab === 'site' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2"
          >
            <button
              onClick={handleSaveSite}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-500/40 bg-slate-900 px-5 py-3 text-xs font-black uppercase tracking-widest text-cyan-400 shadow-[0_0_30px_-8px_rgba(34,211,238,0.5)] transition hover:bg-cyan-500/10 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Unsaved changes · Save
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {pages.length > 0 && tab === 'pages' && originalPages.length !== pages.length && (
        <div className="mt-4 flex items-center gap-2 text-[11px] font-semibold text-amber-300/80">
          <Trash2 size={14} />
          A page was added or removed. Reload to see the server state.
        </div>
      )}
    </div>
  );
}

export default SeoSetupComponent;
